import i18n from '@/i18n';
import { GraphRequestError } from '@/services/graph/graph-request-error';
import { graphMailService } from '@/services/graph/graph-mail-service';
import { acquireMailToken } from '@/services/graph/mail-token';
import { sharePointDriveService } from '@/services/sharepoint-drive/sharepoint-drive-service';
import { inject, provide, ref, type InjectionKey, type Ref } from 'vue';
import { computeToolMode } from '../../permissions/modes';
import type { FileManagerPermissionsState } from '../../permissions/useFileManagerPermissions';
import type { FileSystemItem } from '../../types';
import {
  createMailDraft,
  fetchAttachmentBytes,
  type CreateMailDraftDeps,
  type MailItemStatus,
  type MailProgressItem,
} from '../tools/outlook-mail/createMailDraft';

/**
 * 'running' plus createMailDraft's own three terminal outcomes. The modal is non-dismissible in exactly
 * one of these states, which is why 'running' is part of the same union rather than a separate boolean.
 */
export type MailDraftPhase = 'running' | 'completed' | 'cancelled' | 'failed';

export type FileManagerMailDraftState = {
  isOpen: Ref<boolean>;
  items: Ref<MailProgressItem[]>;
  phase: Ref<MailDraftPhase>;
  /** Non-null once a draft exists — the target of "Öppna i Outlook". */
  webLink: Ref<string | null>;
  /** Set only for phase 'failed': why the whole run could not produce a draft. */
  failureReason: Ref<string | null>;
  /** True only when the failure was token acquisition, which a fresh gesture can retry. */
  canRetry: Ref<boolean>;
  /** Whether the mail tool may act on an item at this path. */
  canMailAt: (path: string) => boolean;
  start: (target: FileSystemItem[]) => void;
  retry: () => void;
  cancel: () => void;
  dismiss: () => void;
};

export const fileManagerMailDraftKey: InjectionKey<FileManagerMailDraftState> =
  Symbol('fileManagerMailDraft');

/**
 * `permissions` is taken as an explicit param rather than self-injected via
 * useFileManagerPermissions(): this factory is called directly in FileManager.vue's own setup(), the
 * same instance that provides the mounts it derives from, and inject() resolves against the PARENT
 * instance's provides — so a component can never inject its own provide(). Identical reasoning to
 * provideFileManagerDelete's.
 *
 * There is deliberately no `data/use*Mutation.ts` hook behind this. Every file in the file manager's
 * `data/` directory exists to invalidate directory query keys after changing the file system, and
 * creating a mail draft changes nothing in SharePoint — there is no key to invalidate. A mutation hook
 * would assert a cache relationship that does not exist, and vue-query's isPending/error say nothing
 * useful about multi-phase per-file progress. A choice, not an oversight.
 */
export const provideFileManagerMailDraft = (
  permissions: Pick<FileManagerPermissionsState, 'permissionsAt'>,
) => {
  const { t } = i18n.global;

  const isOpen = ref(false);
  const items = ref<MailProgressItem[]>([]);
  // Only meaningful while `isOpen`; the initial value is never rendered. It matters only that it is not
  // 'running', so `dismiss()` is not a no-op before the first run.
  const phase = ref<MailDraftPhase>('completed');
  const webLink = ref<string | null>(null);
  const failureReason = ref<string | null>(null);
  const canRetry = ref(false);

  // Identifies the run that owns the modal. A late result from a superseded run (retry pressed while an
  // old promise was still settling) is discarded rather than overwriting the current one.
  let abortController: AbortController | null = null;
  let lastTarget: FileSystemItem[] = [];

  const canMailAt = (path: string) =>
    computeToolMode(permissions.permissionsAt(path), 'tool:outlook-mail-copy') ===
    'enabled';

  const onStatusChange = (
    path: string,
    status: MailItemStatus,
    reason?: string,
  ) => {
    items.value = items.value.map((item) =>
      item.path === path ? { ...item, status, failureReason: reason } : item,
    );
  };

  const deps: CreateMailDraftDeps = {
    // Goes through the same seam graphMailService uses, so the up-front R5 acquisition and every later
    // call resolve against the same tenant — see mail-token.ts.
    acquireToken: () => acquireMailToken(),
    createDraft: () => graphMailService.createDraft(),
    getDownloadUrl: (item, signal) => {
      if (!item.driveId || !item.id) {
        // Addressed by id like every other id-based operation here. A row whose listing has not resolved
        // its Graph coordinates is a real per-file failure with a reportable reason, not something to
        // retry — 404 is both, and is not retryable.
        throw new GraphRequestError('File is missing its Graph coordinates', 404);
      }
      return sharePointDriveService.getDownloadUrl(item.driveId, item.id, signal);
    },
    fetchBytes: fetchAttachmentBytes,
    attachSmall: (messageId, name, contentType, blob, signal) =>
      graphMailService.attachSmall(messageId, name, contentType, blob, signal),
    attachLarge: (messageId, name, contentType, blob, signal) =>
      graphMailService.attachLarge(messageId, name, contentType, blob, signal),
    // No signal, by design — see deleteDraft in graph-mail-service.ts.
    deleteDraft: (messageId) => graphMailService.deleteDraft(messageId),
  };

  const start = (target: FileSystemItem[]) => {
    if (phase.value === 'running') return;
    if (target.length === 0) return;
    // Cheap insurance against a stale entry point calling through after a permission change — the menu
    // layer already withholds the tool entirely. Same guard, and the same `every`, as requestDelete's.
    if (!target.every((item) => canMailAt(item.path))) return;

    const controller = new AbortController();
    abortController = controller;
    lastTarget = target;

    // Kicked off BEFORE this function touches any state, and before it awaits anything at all:
    // createMailDraft's first statement is the Mail.ReadWrite acquisition, and MSAL's fallback for a
    // scope the app login never requested is window.open. Moved after an await, transient user
    // activation is gone and Firefox/Safari block the consent popup (R5).
    const run = createMailDraft(target, deps, controller.signal, onStatusChange);

    items.value = target.map((item) => ({
      path: item.path,
      name: item.name,
      status: 'queued' as const,
    }));
    phase.value = 'running';
    webLink.value = null;
    failureReason.value = null;
    canRetry.value = false;
    isOpen.value = true;

    void run.then(
      (result) => {
        if (controller !== abortController) return;
        phase.value = result.phase;
        if (result.phase === 'completed') {
          webLink.value = result.webLink;
        } else if (result.phase === 'failed') {
          failureReason.value = result.reason;
          canRetry.value = result.canRetry;
        }
      },
      // createMailDraft catches everything it can classify, so this is a genuine "should not happen"
      // path. Left in anyway: the alternative is a modal stuck on 'running' forever, non-dismissible.
      () => {
        if (controller !== abortController) return;
        phase.value = 'failed';
        failureReason.value = t(
          'fileManager.tools.outlookMail.createMailDraft.failedGeneric',
        );
        canRetry.value = false;
      },
    );
  };

  /**
   * A fresh user gesture, which is the entire point: the only retryable failure is a blocked or declined
   * consent popup, and only a real click can open one.
   */
  const retry = () => {
    if (!canRetry.value) return;
    start(lastTarget);
  };

  /**
   * Aborts in-flight work; the draft deletion happens inside createMailDraft's teardown, which is why
   * the modal stays open and non-dismissible until the run reports 'cancelled'. Saying the draft is gone
   * before it is would be a claim nobody verified.
   */
  const cancel = () => {
    abortController?.abort();
  };

  /**
   * Closing the modal in a terminal state. Closing WITHOUT opening leaves the draft in place — only
   * cancellation deletes it (D8), which is also why this is not wired to the same handler as cancel.
   *
   * A no-op while running: no Escape, no click-outside, no header × during the run. Silent dismissal of a
   * long operation is how you get users who do not know whether their mail exists.
   */
  const dismiss = () => {
    if (phase.value === 'running') return;
    isOpen.value = false;
  };

  // Deliberately NO onBeforeUnmount abort, unlike provideFileManagerTransfer's. Aborting is what deletes
  // the draft, and navigating away is not cancelling: the user asked for a draft, so letting the run
  // finish and land it in their mailbox is the friendly outcome. The work is bounded (at most
  // `concurrency` blobs, then done) and the status callbacks only write to plain refs nobody is reading —
  // there is no torn-down DOM to touch.

  const state: FileManagerMailDraftState = {
    isOpen,
    items,
    phase,
    webLink,
    failureReason,
    canRetry,
    canMailAt,
    start,
    retry,
    cancel,
    dismiss,
  };
  provide(fileManagerMailDraftKey, state);
  return state;
};

export const useFileManagerMailDraft = () => {
  const state = inject(fileManagerMailDraftKey);
  if (!state) {
    throw new Error(
      'useFileManagerMailDraft() must be called within a component tree provided by provideFileManagerMailDraft()',
    );
  }
  return state;
};
